/**
 * Recognise "your session is no longer valid" failures.
 *
 * Why this exists: changing a password revokes the user's refresh tokens
 * server-side, but the browser keeps the old session in localStorage. The app
 * then hits a mix of results — PostgREST still accepts the (signed, unexpired)
 * access token, while edge functions call `auth.getUser(token)` and get a 401
 * because the session is gone. The subscription check fell into its "no
 * subscription" branch, so `hasProducerAccess` went false and the route guard
 * bounced the user to /upgrade ("Your trial has ended") — a paying producer
 * being told to buy a plan, with no way back to the dashboard.
 *
 * A dead session must lead to "log in again", never to "you have no access".
 * This module answers only the question "is this failure an auth failure?" so
 * that decision can be made from one place, without any side effects.
 */

/**
 * Postgrest error codes for a rejected / expired JWT.
 *
 * Note 42501 (insufficient_privilege) is deliberately absent: that's a normal
 * RLS rejection on a valid session — the soft-delete path hits it by design —
 * and signing the user out for it would be a worse bug than the one this
 * module fixes.
 */
const POSTGREST_AUTH_CODES = new Set(["PGRST301", "PGRST302"]);

/**
 * Substrings GoTrue / PostgREST use for an invalid or revoked session.
 * Matched case-insensitively against the error message.
 */
const AUTH_MESSAGE_PATTERNS = [
  "jwt expired",
  "jwt is expired",
  "invalid jwt",
  "invalid claim",
  "invalid refresh token",
  "refresh token not found",
  "refresh_token_not_found",
  "session_not_found",
  "session not found",
  "session from session_id claim in jwt does not exist",
  "user from sub claim in jwt does not exist",
  "bad_jwt",
];

function messageLooksLikeAuthFailure(message: string): boolean {
  const lower = message.toLowerCase();
  return AUTH_MESSAGE_PATTERNS.some((pattern) => lower.includes(pattern));
}

/**
 * True when the failure means the session is no longer usable and the user has
 * to sign in again.
 *
 * Deliberately narrow — only 401 counts, never 403 or 42501. A network drop,
 * an offline device, a 500 from an edge function, or a plain permission denial
 * on a healthy session must NOT be read as an expired session: signing the
 * user out on those would be worse than the bug this guards against.
 */
export function isAuthSessionError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;

  // supabase-js FunctionsHttpError carries the raw Response on `.context`.
  const context = (error as { context?: unknown }).context;
  if (context instanceof Response) {
    if (context.status === 401) return true;
  } else if (context && typeof context === "object") {
    // Some supabase-js versions expose a plain object instead of a Response.
    if ((context as { status?: unknown }).status === 401) return true;
  }

  if ((error as { status?: unknown }).status === 401) return true;

  const code = (error as { code?: unknown }).code;
  if (typeof code === "string" && POSTGREST_AUTH_CODES.has(code)) return true;

  if ((error as { name?: unknown }).name === "AuthSessionMissingError") return true;

  const message = (error as { message?: unknown }).message;
  if (typeof message === "string" && messageLooksLikeAuthFailure(message)) return true;

  return false;
}

/** Toast copy used wherever an expired session is reported to the user. */
export const SESSION_EXPIRED_TITLE = "Session expired";
export const SESSION_EXPIRED_DESCRIPTION =
  "Your session is no longer valid — please sign in again.";
